import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton } from "@mui/material"
import { CloseOutlined } from "@mui-symbols-material/w300"
import type { FunctionComponent } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { useUpdateProduct } from "../hooks/useUpdateProduct"
import { convertToProductInfoFormValues } from "../utils/productForm"
import { toProductSpecificsUpdateInput } from "../utils/toProductSpecificsUpdateInput"

import type { ProductSpecificsFormValues } from "./productFormSchema"
import { ProductSpecificsFormSchema } from "./productFormSchema"
import { ProductSpecificsForm } from "./ProductSpecificsForm"

import { Button } from "@/components/common/Button"
import type { Product } from "@/graphql/codegen/graphql"
import { ProductFormInfoItem } from "@/graphql/codegen/graphql"
import { pickDirtyFieldValues } from "@/utils/forms"

interface EditProductSpecificsDialogProps {
  product: Product
  onClose: () => void
  onSubmit: () => void
}

const EditProductSpecificsDialog: FunctionComponent<EditProductSpecificsDialogProps> = ({
  product,
  onClose,
  onSubmit,
}) => {
  const methods = useForm<ProductSpecificsFormValues>({
    defaultValues: {
      category: product.classification?.categoryDetail?.categoryId ?? "",
      classification: product.classification?.classificationId ?? "",
      productManager: {
        id: product?.classification?.productManager?.contactId ?? "",
        name: `${product?.classification?.productManager?.firstName ?? ""} ${product?.classification?.productManager?.lastName ?? ""}`.trim(),
      },
      productFormInfo:
        Object.keys(ProductFormInfoItem).find(
          (key) => ProductFormInfoItem[key as keyof typeof ProductFormInfoItem] === product.productFormInfo
        ) ?? "",
      uomId: product.uomId ?? "",
    },
    resolver: zodResolver(ProductSpecificsFormSchema),
    mode: "all",
  })
  const {
    formState: { isDirty, isValid, isSubmitting, dirtyFields },
    handleSubmit,
  } = methods
  const canSubmit = isDirty && isValid && !isSubmitting
  const { update } = useUpdateProduct({ onSuccess: onSubmit })
  const productInfo = convertToProductInfoFormValues(product)

  const submitForm = async (formValues: ProductSpecificsFormValues) => {
    await update(toProductSpecificsUpdateInput(product.productId, pickDirtyFieldValues(formValues, dirtyFields)))
  }

  return (
    <Dialog open onClose={onClose} fullWidth>
      <DialogTitle className='pl-10 font-normal text-primary'>Edit Product Specifics</DialogTitle>
      <IconButton aria-label='close' onClick={onClose} className='absolute right-4 top-3 text-primary'>
        <CloseOutlined />
      </IconButton>
      <Divider />
      <DialogContent classes={{ root: "p-10" }}>
        <FormProvider {...methods}>
          <ProductSpecificsForm productInfo={productInfo} />
        </FormProvider>
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button appearance='outlined' onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={!canSubmit} onClick={handleSubmit(submitForm)}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EditProductSpecificsDialog
